import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import GlobalCollapsibleButton from './GlobalCollapsibleButton';
import { GlobalButton } from './GlobalButton';
import GlobalSidebar from './GlobalSidebar';
import { GlobalTextarea } from './GlobalTextarea';
import { useTheme } from '../context/ThemeProvider';
import {
  deleteEntityDocumentRecord,
  saveEntityDocument,
} from '../lib/entityDocumentFirestore';
import {
  deleteEntityDocumentFile,
  entityDocumentFileError,
  ENTITY_DOCUMENT_ACCEPT,
  uploadEntityDocumentFile,
  entityDocumentStoragePath,
} from '../lib/entityDocumentStorage';
import {
  extractEntityDocumentText,
  extractEntityDocumentTextErrorMessage,
} from '../lib/extractEntityDocumentText';
import { useEntityDocuments } from '../hooks/useEntityDocuments';
import type { EntityDocument, EntityDocumentEntityType } from '../types';
import { formatDateLong } from '../utils/formatters';
import {
  fileExtension,
  fileTypeIconSrc,
  fileTypeKind,
  fileTypeLabel,
  formatFileSize,
} from '../utils/fileTypeIcon';
import { newId } from '../utils/id';
import styles from './EntityDocumentsPanel.module.css';

export type EntityDocumentUploadControl = {
  openFilePicker: () => void;
  uploading: boolean;
};

type Props = {
  userId: string;
  entityType: EntityDocumentEntityType;
  entityId: string;
  entityLabel?: string;
  compact?: boolean;
  onUploadControlChange?: (control: EntityDocumentUploadControl | null) => void;
};

function DocumentPageCard({
  document,
  isDarkMode,
  isSelected,
  onSelect,
}: {
  document: EntityDocument;
  isDarkMode: boolean;
  isSelected: boolean;
  onSelect: (document: EntityDocument) => void;
}) {
  const kind = fileTypeKind(document.fileName, document.contentType);
  const iconSrc = fileTypeIconSrc(kind, isDarkMode);
  const extractedChars = document.extractedText?.length ?? 0;
  const hasNotes = Boolean(document.notes?.trim());

  return (
    <button
      type="button"
      className={`${styles.pageCard} ${isSelected ? styles.pageCardSelected : ''}`}
      onClick={() => onSelect(document)}
      aria-pressed={isSelected}
      aria-label={`Open ${document.fileName}`}
    >
      <div className={styles.pageSheet} aria-hidden>
        <div className={styles.pageSheetHeader}>
          <img src={iconSrc} alt="" width={20} height={20} className={styles.pageSheetIcon} />
          <span className={styles.pageSheetType}>{fileTypeLabel(kind)}</span>
        </div>
        <div className={styles.pageSheetLines}>
          <span />
          <span />
          <span />
          <span className={styles.pageSheetLineShort} />
        </div>
        {extractedChars > 0 ? (
          <span className={styles.pageIndexedBadge} title="Indexed for search">
            {extractedChars >= 1000
              ? `${Math.round(extractedChars / 1000)}k indexed`
              : `${extractedChars} indexed`}
          </span>
        ) : (
          <span className={styles.pageUnindexedBadge}>Not indexed</span>
        )}
      </div>

      <div className={styles.pageCardFooter}>
        <p className={styles.pageCardTitle} title={document.fileName}>
          {document.fileName}
        </p>
        <p className={styles.pageCardMeta}>
          <span>{formatFileSize(document.sizeBytes)}</span>
          <span>{formatDateLong(document.uploadedAt)}</span>
          {hasNotes ? <span className={styles.pageCardNotesDot} title="Has notes" /> : null}
        </p>
      </div>
    </button>
  );
}

function DocumentDetailSidebar({
  document,
  isDarkMode,
  onClose,
  onDelete,
  onSaveNotes,
  onReextract,
}: {
  document: EntityDocument;
  isDarkMode: boolean;
  onClose: () => void;
  onDelete: (document: EntityDocument) => Promise<void>;
  onSaveNotes: (document: EntityDocument, notes: string) => Promise<void>;
  onReextract: (document: EntityDocument) => Promise<void>;
}) {
  const [deleting, setDeleting] = useState(false);
  const [reextracting, setReextracting] = useState(false);
  const [notesDraft, setNotesDraft] = useState(document.notes ?? '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const kind = fileTypeKind(document.fileName, document.contentType);
  const iconSrc = fileTypeIconSrc(kind, isDarkMode);
  const ext = fileExtension(document.fileName).toUpperCase();
  const extractedChars = document.extractedText?.length ?? 0;

  async function handleDelete() {
    if (deleting) return;
    setDeleting(true);
    setFeedback(null);
    try {
      await onDelete(document);
      onClose();
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Delete failed.');
      setDeleting(false);
    }
  }

  async function handleReextract() {
    if (reextracting) return;
    setReextracting(true);
    setFeedback(null);
    try {
      await onReextract(document);
      setFeedback('Text re-extracted.');
    } catch (err) {
      setFeedback(extractEntityDocumentTextErrorMessage(err));
    } finally {
      setReextracting(false);
    }
  }

  async function handleSaveNotes() {
    if (savingNotes) return;
    setSavingNotes(true);
    setFeedback(null);
    try {
      await onSaveNotes(document, notesDraft);
      setFeedback('Notes saved.');
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Failed to save notes.');
    } finally {
      setSavingNotes(false);
    }
  }

  return (
    <div className="sidebarFormStack">
      <div className={styles.detailHeader}>
        <div className={styles.detailIconWrap} aria-hidden>
          <img src={iconSrc} alt="" width={24} height={24} />
        </div>
        <div className={styles.detailTitleBlock}>
          <h2 className={`sidebarFormTitle ${styles.detailTitle}`}>{document.fileName}</h2>
          <p className={styles.detailMeta}>
            <span>{fileTypeLabel(kind)}</span>
            {ext ? <span>{ext}</span> : null}
            <span>{formatFileSize(document.sizeBytes)}</span>
            <span>Uploaded {formatDateLong(document.uploadedAt)}</span>
          </p>
        </div>
      </div>

      <div className={styles.detailActions}>
        <GlobalButton
          type="button"
          autoWidth
          onClick={() => window.open(document.downloadUrl, '_blank', 'noopener,noreferrer')}
        >
          Open file
        </GlobalButton>
        <GlobalButton
          type="button"
          variant="secondary"
          autoWidth
          isLoading={reextracting}
          disabled={reextracting || deleting}
          onClick={handleReextract}
        >
          Re-extract
        </GlobalButton>
        <GlobalButton
          type="button"
          variant="secondary"
          destructive
          autoWidth
          isLoading={deleting}
          disabled={deleting || reextracting}
          onClick={handleDelete}
        >
          Delete
        </GlobalButton>
      </div>

      <p className={styles.indexMeta}>
        {extractedChars > 0
          ? `${extractedChars.toLocaleString()} characters indexed for search`
          : 'No text extracted yet — re-extract or upload a text-based file'}
      </p>

      {document.extractedText ? (
        <GlobalCollapsibleButton title="Extracted text preview" isOpen={extractedChars < 800}>
          <pre className={styles.extractPreview}>{document.extractedText.slice(0, 4000)}</pre>
        </GlobalCollapsibleButton>
      ) : null}

      <div className={styles.notesBlock}>
        <GlobalTextarea
          label="Notes"
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          rows={4}
          maxLength={1000}
        />
        <p className={`${styles.feedback} muted`}>
          {notesDraft.length}/1000 — included in intelligence search
        </p>
        <GlobalButton
          type="button"
          variant="secondary"
          autoWidth
          isLoading={savingNotes}
          disabled={savingNotes || notesDraft === (document.notes ?? '')}
          onClick={handleSaveNotes}
        >
          Save notes
        </GlobalButton>
      </div>

      {feedback ? <p className={`${styles.feedback} muted`}>{feedback}</p> : null}
    </div>
  );
}

export function EntityDocumentsPanel({
  userId,
  entityType,
  entityId,
  compact = false,
  onUploadControlChange,
}: Props) {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { documents, loading, error } = useEntityDocuments(userId, entityType, entityId);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  useEffect(() => {
    if (!onUploadControlChange) return;
    onUploadControlChange({ openFilePicker, uploading });
    return () => onUploadControlChange(null);
  }, [onUploadControlChange, openFilePicker, uploading]);

  const showInlineUploadButton = compact && !onUploadControlChange;

  const selectedDocument =
    selectedDocumentId != null
      ? documents.find((document) => document.id === selectedDocumentId) ?? null
      : null;

  async function persistDocument(document: EntityDocument): Promise<void> {
    await saveEntityDocument(userId, document);
  }

  async function handleUpload(file: File) {
    const validationError = entityDocumentFileError(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setUploading(true);
    setUploadError(null);

    const documentId = newId();
    const now = new Date().toISOString();

    try {
      const { storagePath, downloadUrl } = await uploadEntityDocumentFile(
        userId,
        entityType,
        entityId,
        documentId,
        file
      );

      let extractedText = '';
      try {
        extractedText = await extractEntityDocumentText(storagePath);
      } catch {
        extractedText = '';
      }

      await persistDocument({
        id: documentId,
        userId,
        entityType,
        entityId,
        fileName: file.name,
        storagePath,
        downloadUrl,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        extractedText: extractedText.trim() ? extractedText : undefined,
        uploadedAt: now,
        updatedAt: now,
      });
    } catch (err) {
      await deleteEntityDocumentFile(
        entityDocumentStoragePath(userId, entityType, entityId, documentId, file.name)
      );
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) void handleUpload(file);
  }

  async function handleDelete(document: EntityDocument) {
    await deleteEntityDocumentFile(document.storagePath);
    await deleteEntityDocumentRecord(userId, document.id);
    if (selectedDocumentId === document.id) {
      setSelectedDocumentId(null);
    }
  }

  async function handleReextract(document: EntityDocument) {
    const extractedText = await extractEntityDocumentText(document.storagePath);
    await persistDocument({
      ...document,
      extractedText: extractedText.trim() ? extractedText : undefined,
      updatedAt: new Date().toISOString(),
    });
  }

  async function handleSaveNotes(document: EntityDocument, notes: string) {
    await persistDocument({
      ...document,
      notes: notes.trim() ? notes.trim() : undefined,
      updatedAt: new Date().toISOString(),
    });
  }

  return (
    <div className={`${styles.panel} ${compact ? styles.panelCompact : ''}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={ENTITY_DOCUMENT_ACCEPT}
        className={styles.fileInput}
        onChange={handleFileChange}
        disabled={uploading}
      />

      {showInlineUploadButton ? (
        <div className={styles.uploadRow}>
          <GlobalButton
            type="button"
            variant="secondary"
            autoWidth
            isLoading={uploading}
            disabled={uploading}
            onClick={openFilePicker}
            icon={
              <img
                src={
                  isDarkMode
                    ? '/assets/icons/icloud.and.arrow.up.svg'
                    : '/assets/icons/icloud.and.arrow.up.black.svg'
                }
                alt=""
                width={16}
                height={16}
              />
            }
          >
            Upload document
          </GlobalButton>
        </div>
      ) : null}

      {uploadError ? (
        <p className={styles.error} role="alert">
          {uploadError}
        </p>
      ) : null}

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="muted">Loading documents…</p>
      ) : documents.length === 0 ? (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyTitle}>No documents yet</h3>
          <p className={`${styles.emptyCopy} muted`}>
            Attach contracts, statements, appraisals, or exports. They appear here and feed your
            intelligence index after the next re-index.
          </p>
        </div>
      ) : (
        <ul
          className={`${styles.grid} ${compact ? styles.gridCompact : ''}`}
          aria-label="Uploaded documents"
        >
          {documents.map((document) => (
            <li key={document.id}>
              <DocumentPageCard
                document={document}
                isDarkMode={isDarkMode}
                isSelected={selectedDocumentId === document.id}
                onSelect={(next) => setSelectedDocumentId(next.id)}
              />
            </li>
          ))}
        </ul>
      )}

      <GlobalSidebar
        isOpen={selectedDocument !== null}
        onClose={() => setSelectedDocumentId(null)}
      >
        {selectedDocument ? (
          <DocumentDetailSidebar
            key={selectedDocument.id}
            document={selectedDocument}
            isDarkMode={isDarkMode}
            onClose={() => setSelectedDocumentId(null)}
            onDelete={handleDelete}
            onReextract={handleReextract}
            onSaveNotes={handleSaveNotes}
          />
        ) : null}
      </GlobalSidebar>
    </div>
  );
}
